import { Icons } from "assets";
import classNames from "classnames";
import { useFormik } from "formik";
import { useEffect, useRef, useState } from "react";
import CustomButton from "shared/components/common/customButton";
import CustomSelect from "shared/components/common/customSelect";
import CustomTextArea from "shared/components/common/customTextArea";
import { toastMessage } from "shared/components/common/toast";
import { EditOptionService } from "shared/services/questionnaire";
import { EditOptionVS } from "shared/utils/validation";
import styles from "./style.module.scss";
import { JobRole } from "shared/utils/enum";

const initialValues: OptionType = {
  short_text: "",
  long_text: "",
  option: null,
  role: null,
  dropdown: null,
};

interface ModifyOptionProps {
  data: any;
}

const ModifyOption = ({ data }: ModifyOptionProps) => {
  const roleRef = useRef<any>(JobRole.management);
  const [dropdowns, setDropdowns] = useState<any>([]);
  const [options, setOptions] = useState<any>([]);
  const [isShortTextEdit, setIsShortTextEdit] = useState<boolean>(false);
  const [isLongTextEdit, setIsLongTextEditEdit] = useState<boolean>(false);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: EditOptionVS,
    onSubmit: () => {
      handleEditQuestion();
    },
  });

  const {
    values,
    touched,
    errors,
    isSubmitting,
    setFieldValue,
    handleSubmit,
    handleChange,
    setSubmitting,
  } = formik;

  const roleOptions = [
    {
      title: "Manager",
      Icon: null,
      action: (index: number) => {
        roleRef.current = JobRole.management;
        setFieldValue("role", {
          title: "Manager",
          value: JobRole.management,
        });
        setFieldValue("dropdown", null);
        setFieldValue("option", null);
        setFieldValue("short_text", "");
        setFieldValue("long_text", "");
      },
    },
    {
      title: "Individual Contributor",
      Icon: null,
      action: (index: number) => {
        roleRef.current = JobRole.individualContributor;
        setFieldValue("role", {
          title: "Individual Contributor",
          value: JobRole.individualContributor,
        });
        setFieldValue("dropdown", null);
        setFieldValue("option", null);
        setFieldValue("short_text", "");
        setFieldValue("long_text", "");
      },
    },
  ];

  const handleOptions = (options: any[]) => {
    let tempOptions = [];
    for (let i = 0; i < options?.length; i++) {
      let option = options[i];
      let optionObj = {
        title: option?.short_des,
        value: option?.id,
        action: () => {
          setFieldValue("option", {
            title: option?.short_des,
            value: option?.id,
          });
          setFieldValue("short_text", option?.short_des);
          setFieldValue("long_text", option?.long_des);
        },
      };
      tempOptions.push(optionObj);
    }
    setOptions(tempOptions);
  };

  const handleQuestionnaire = () => {
    let tempArr = [];

    for (let i = 0; i < data?.dropdowns?.length; i++) {
      let dropdown = data?.dropdowns[i];

      if (i === 0) {
        setFieldValue("role", {
          title: "Manager",
          value: JobRole.management,
        });
        setFieldValue("dropdown", {
          title: dropdown?.text,
          value: dropdown?.id,
        });
        setFieldValue("option", {
          title: dropdown?.options_2?.[0]?.short_des,
          value: dropdown?.options_2?.[0]?.id,
        });
        setFieldValue("short_text", dropdown?.options_2?.[0]?.short_des);
        setFieldValue("long_text", dropdown?.options_2?.[0]?.long_des);
        handleOptions(dropdown?.options_2);
      }
      let obj = {
        title: dropdown?.text,
        value: dropdown?.id,
        action: () => {
          setFieldValue("dropdown", {
            title: dropdown?.text,
            value: dropdown?.id,
          });
          if (roleRef.current === JobRole.management) {
            handleOptions(dropdown?.options_2);
            setFieldValue("option", {
              title: dropdown?.options_2?.[0]?.short_des,
              value: dropdown?.options_2?.[0]?.id,
            });
            setFieldValue("short_text", dropdown?.options_2?.[0]?.short_des);
            setFieldValue("long_text", dropdown?.options_2?.[0]?.long_des);
          } else {
            handleOptions(dropdown?.options_1);
            setFieldValue("option", {
              title: dropdown?.options_1?.[0]?.short_des,
              value: dropdown?.options_1?.[0]?.id,
            });
            setFieldValue("short_text", dropdown?.options_1?.[0]?.short_des);
            setFieldValue("long_text", dropdown?.options_1?.[0]?.long_des);
          }
        },
      };

      tempArr.push(obj);
    }
    setDropdowns(tempArr);
  };

  const handleEditQuestion = () => {
    setSubmitting(true);
    EditOptionService(
      { short_text: values.short_text, long_text: values.long_text },
      values.option.value
    )
      .then(() => {
        toastMessage("success", "Question updated successfully");
        setIsShortTextEdit(false);
        setIsLongTextEditEdit(false);
      })
      .catch((err) => {
        toastMessage("error", err?.response?.data?.error);
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  useEffect(() => {
    if (data) {
      handleQuestionnaire();
    }
    // eslint-disable-next-line
  }, [data]);

  return (
    <div className={classNames("flex flex-col gap-3")}>
      <div
        className={classNames("flex flex-col sm:flex-row items-start gap-6")}
      >
        <div className={classNames("w-full sm:w-2/6")}>
          <CustomSelect
            label="Role Type"
            placeholder="Select Role"
            options={roleOptions}
            value={values.role?.title}
            //@ts-ignore
            error={touched.role && errors?.role ? errors?.role : ""}
            disabled={isSubmitting}
          />
          <CustomSelect
            label="Category"
            placeholder="Select Category"
            options={dropdowns}
            value={values.dropdown?.title}
            //@ts-ignore
            error={touched.dropdown && errors?.dropdown ? errors?.dropdown : ""}
            direction="bottom"
            disabled={isSubmitting}
          />
          <CustomSelect
            label="Option"
            placeholder="Select Option"
            options={options}
            value={values.option?.title}
            //@ts-ignore
            error={touched.option && errors?.option ? errors?.option : ""}
            direction="bottom"
            disabled={isSubmitting}
          />
        </div>
        <div className={classNames("w-full sm:w-4/6 flex flex-col gap-3")}>
          <div className={classNames("relative")}>
            <CustomTextArea
              label="Short Definition"
              placeholder="Enter Definition"
              required
              value={values.short_text}
              customInputContainer={classNames(styles.textAreaContainer)}
              onChange={handleChange("short_text")}
              disabled={!isShortTextEdit || isSubmitting}
              error={
                touched.short_text &&
                //@ts-ignore
                errors?.short_text
                  ? errors?.short_text
                  : ""
              }
            />
            <div
              className={classNames(styles.editContainer)}
              onClick={() => {
                setIsShortTextEdit(!isShortTextEdit);
              }}
            >
              <Icons.Pencil />
              <label>Edit</label>
            </div>
          </div>
          <div className={classNames("relative")}>
            <CustomTextArea
              label="Long Definition"
              placeholder="Enter Definition"
              required
              value={values.long_text}
              customInputContainer={classNames(styles.textAreaContainer2)}
              onChange={handleChange("long_text")}
              disabled={!isLongTextEdit || isSubmitting}
              error={
                touched.long_text &&
                //@ts-ignore
                errors?.long_text
                  ? errors?.long_text
                  : ""
              }
            />
            <div
              className={classNames(styles.editContainer)}
              onClick={() => {
                setIsLongTextEditEdit(!isLongTextEdit);
              }}
            >
              <Icons.Pencil />
              <label>Edit</label>
            </div>
          </div>
          <CustomButton
            title="Save Changes"
            containerStyle={classNames("self-end")}
            onClick={() => {
              handleSubmit();
            }}
            loading={isSubmitting}
            disabled={isSubmitting}
          />
        </div>
      </div>
    </div>
  );
};

export default ModifyOption;
